import { computed, watch } from 'vue';
import { isProduction } from '../utils/env';
import type { PluginOptions } from './type';
import { useMenuStore, useSettingsStore, useUserStore } from '../stores';
import { isNavigationFailure } from 'vue-router';

/** 神策配置 */
const sensorsConfig = computed(() => {
  const settingsStore = useSettingsStore();
  return settingsStore.settings.sensorsConfig;
});

/** 是否开启神策埋点 */
const isOpenSensors = computed(() => {
  return !!sensorsConfig.value?.system_code;
});

// 适用于pageview
function getPageTitle(to?: { path?: string; meta: { title?: string } }) {
  const settingsStore = useSettingsStore();
  const menuStore = useMenuStore();
  const {
    system,
    route: { baseURL },
  } = settingsStore.settings;

  let pathname = to?.path ?? window.location.pathname;
  if (baseURL) {
    pathname = pathname.replace(baseURL, '');
  }
  const metaTitle = (to?.meta?.title as string) || '';
  const title: string | undefined = menuStore.menuPathMap[pathname]?.title || metaTitle;
  const $title = system.getDocumentTitle?.(title);
  return {
    $title,
    menu_name: title,
    menu_url: window.location.href,
  };
}

export const onSensorsWebClick = (options: {
  title: string;
  source: string;
  menuLevel?: string;
  from?: { $url: string; $url_path: string };
  to?: { $url: string; $url_path: string };
}) => {
  sensorsConfig.value.onWebClick?.({
    ...options,
    menu_name: options.title,
    menu_url: options.to?.$url,
  });
};

const options: PluginOptions = {
  async onVuePluginRegister({ router }) {
    if (!isOpenSensors.value) {
      return;
    }

    const { default: sensors } = await import('sa-sdk-javascript');
    const { isManualReport, initData = {} } = sensorsConfig.value;
    const menuStore = useMenuStore();

    // 兼容Promise及异步函数
    let { server_url, ...otherConfig } = initData;
    if (typeof server_url === 'function') {
      server_url = await server_url();
    } else if (server_url instanceof Promise) {
      server_url = await server_url;
    } else if (!server_url) {
      server_url = `https://ubs.sf-express.com/sa?project=${isProduction ? 'digital' : 'digitalgray'}`;
    }

    sensors.init({
      server_url,
      is_track_single_page: false, // 由 router.afterEach 手动采集，避免重复和时序问题
      use_client_time: false,
      send_type: 'beacon',
      show_log: false,
      heatmap: {
        // 是否开启点击图，default 表示开启，自动采集 $WebClick 事件，可以设置 'not_collect' 表示关闭。
        clickmap: isManualReport ? 'not_collect' : 'default',
        // 是否开启触达图，not_collect 表示关闭，不会自动采集 $WebStay 事件，可以设置 'default' 表示开启。
        scroll_notice_map: isManualReport ? 'not_collect' : 'default',
        // 目前若开启全埋点支持任意层级 div 标签功能会发送1个404错误请求
        get_vtrack_config: false,
      },
      ...otherConfig,
    });

    // 页面刷新后及路由变更采集
    if (!isManualReport) {
      if (otherConfig.is_track_single_page) {
        watch(
          () => menuStore.menuPathMap,
          () => {
            sensors.quick('autoTrack', getPageTitle()); // 用于采集 $pageview 事件。
          },
        );
      } else {
        // 首次进入页面
        sensors.quick('autoTrack', getPageTitle(router?.currentRoute?.value));
        // 路由跳转
        router?.afterEach((to, from, failure) => {
          // 只在导航成功时记录，忽略被取消或重定向的导航
          if ((failure && isNavigationFailure(failure)) || to.fullPath === from.fullPath) {
            return;
          }
          sensors.quick('autoTrack', getPageTitle(to));
        });
      }
    }
  },

  async onUserDataUpdate({ operatorNo, operatorName, transitDepotNo, dutyName }) {
    // 神策
    if (!isOpenSensors.value) {
      return;
    }

    const { platform_type, platform_name, system_code, product_name, registerData, profileData } = sensorsConfig.value;
    const userStore = useUserStore();
    let userConfig = {};
    if (typeof profileData === 'function') {
      userConfig = await profileData(userStore.originUserInfo);
    } else {
      userConfig = profileData || {};
    }

    let pageConfig = {};
    if (typeof registerData === 'function') {
      pageConfig = await registerData(userStore.originUserInfo);
    } else {
      pageConfig = registerData || {};
    }

    const sensors = (window as any).sensorsDataAnalytic201505;
    sensors?.login(operatorNo);

    const empInfo = {
      emp_id: operatorNo,
      emp_name: operatorName,
      emp_role: dutyName,
      zone_code: transitDepotNo,
      employee_id: operatorNo,
    };

    // 注册公共信息
    sensors.registerPage({ platform_type, platform_name, system_code, product_name, ...empInfo, ...pageConfig });
    sensors?.setProfile({
      uid: operatorNo,
      ...empInfo,
      ...userConfig,
    });
  },
};

export default options;
